import { PropertyPath, get, isNil, isPlainObject, set } from "lodash";

export type NestedKeyOf<ObjectType extends object> = {
  [Key in keyof ObjectType & (string | number)]: ObjectType[Key] extends object
    ? `${Key}` | `${Key}.${NestedKeyOf<ObjectType[Key]>}`
    : `${Key}`;
}[keyof ObjectType & (string | number)];

/**
 * sets deep value within object, does not copy
 * if root path it returns copied object without setting directly instead
 */
export const setDeepValue = <Obj extends {}>(
  object: Obj,
  path: PropertyPath,
  value: any,
  replace: boolean = false,
): Obj => {
  const isRootPath = path === "" || isNil(path);
  const targetObject = isRootPath ? object : get(object, path, {});
  const parsedValue =
    isPlainObject(value) && isPlainObject(targetObject) && !replace
      ? { ...targetObject, ...value }
      : value;
  return isRootPath ? parsedValue : set(object, path, parsedValue);
};

type Path = (string | number)[];

/**
 * Recursively finds all paths to properties with a specific name within a nested object.
 *
 * This function traverses nested objects and collects all access paths (as arrays of strings)
 * to every occurrence of the specified property name. It does not traverse into arrays.
 *
 * @param obj - The object to search through.
 * @param targetKey - The property name to look for.
 * @param currentPath - Used internally during recursion to track the path.
 * @returns An array of paths, where each path is an array of strings representing the access path to a matching property.
 *
 * Example:
 * ```ts
 *   const obj = { a: { b: { target: 1 }, c: { target: 2 } } };
 *   findPathsToPropertyName(obj, "target");
 *   // => [ ["a", "b", "target"], ["a", "c", "target"] ]
 * ```
 */
export const findPathsToPropertyName = (
  obj: unknown,
  targetKey: string,
  currentPath: Path = [],
): Path[] => {
  if (typeof obj !== "object" || obj === null) return [];

  const paths: Path[] = [];

  for (const key of Object.keys(obj)) {
    const value = (obj as Record<string, unknown>)[key];
    const newPath = [...currentPath, key];

    if (key === targetKey) {
      paths.push(newPath);
    }

    if (typeof value === "object" && value !== null) {
      paths.push(...findPathsToPropertyName(value, targetKey, newPath));
    }
  }
  return paths;
};
